import { MutationTree } from 'vuex';
import { DashboardState } from '../types';
import { DateTime } from 'luxon';
import { state, PENDING_ORDERS_LIMIT } from '../dashboard/dashboard';
import { state as authState } from '../auth/auth';
import { decodeString, decodeDashboardOrders, decodeDashboardOrder } from '@/utils/decoding';
import Vue from 'vue';
import i18n from '@/i18n';

/**
 * Format the start/end dates into the time fields used by calendar events, in the restaurant's zone.
 * Events shorter than the minimum duration are displayed as the minimum duration.
 *
 * @param {string} start
 * @param {string} end
 * @param {number} minDuration
 * @return {{ start: string, end: string, time: string }}
 */
function formatCalendarEventTimes(start: string, end: string, minDuration: number): { start: string, end: string, time: string } {
	const zone = authState.restaurant.menu_configuration?.zone || 'America/Toronto';
	const startDt = DateTime.fromISO(start).setZone(zone);
	const endDt = DateTime.fromISO(end).setZone(zone);
	const eventDuration = endDt.diff(startDt, 'minutes').minutes;
	const displayEndDt = eventDuration > minDuration ? endDt : startDt.plus({ minutes: minDuration });
	return {
		start: startDt.toFormat('yyyy-MM-dd HH:mm'),
		end: displayEndDt.toFormat('yyyy-MM-dd HH:mm'),
		time: startDt.toFormat(i18n.t('orders.management.parent_card.dates.time_format'))
	};
}

/**
 * Formats orders by date into events for the month calendar type
 *
 * @param {OrdersByDate[]} ordersByDate
 * @return {MonthlyCalendarEvent[]}
 */
function formatMonthlyEvents(ordersByDate: OrdersByDate[]): MonthlyCalendarEvent[] {
	return ordersByDate.map((date: OrdersByDate) => {
		return {
			start: date.date,
			end: date.date,
			totalOrders: date.summary.totalOrders,
			statusCounts: date.summary.statusCounts
		};
	});
}

/**
 * Formats orders by date into events for the week/day calendar types
 *
 * @param {OrdersByDate[]} ordersByDate
 * @return {CalendarEvent[]}
 */
function formatDailyEvents(ordersByDate: OrdersByDate[]): CalendarEvent[] {
	const minDuration = state.calendarType === 'day' ? 30 : 60;
	return ordersByDate.flatMap((date: OrdersByDate) => {
		return date.orders?.map((order: DashboardOrder) => {
			const decodedOrder = decodeDashboardOrder(order);
			if (order.cateringEventStart && order.cateringEventEnd) {
				return {
					...decodedOrder,
					name: decodedOrder.cateringEventName || decodedOrder.name,
					...formatCalendarEventTimes(order.cateringEventStart, order.cateringEventEnd, minDuration)
				};
			}
			const orderDate = order.pickup_time || order.eventStartDate || order.created_at;
			return {
				...decodedOrder,
				...formatCalendarEventTimes(orderDate, orderDate, minDuration)
			};
		}) || [];
	});
}

export const mutations: MutationTree<DashboardState> = {
	SET_FILTERS(state, filters: DashboardFilters) {
		Vue.set(state, 'filters', { ...state.filters, ...filters });
	},

	SET_DASHBOARD_DATA(state: DashboardState, dashboardOrdersData: DashboardOrdersResponse) {
		Vue.set(state, 'calendarEvents', state.calendarType === 'month'
			? formatMonthlyEvents(dashboardOrdersData.ordersByDate)
			: formatDailyEvents(dashboardOrdersData.ordersByDate)
		);
		Vue.set(state, 'ordersSummary', dashboardOrdersData.summary);
		Vue.set(state, 'pendingOrdersCount', dashboardOrdersData.pendingOrdersCount);
		Vue.set(state, 'pendingOrders', decodeDashboardOrders(dashboardOrdersData.pendingOrders));

		if (dashboardOrdersData.quotes) {
			Vue.set(state, 'quotes', dashboardOrdersData.quotes.rows.map((quote) => ({
				...quote,
				eventName: decodeString(quote.eventName || '')
			})));
			Vue.set(state, 'quotesCount', dashboardOrdersData.quotes.count);
		}

		if (dashboardOrdersData.inquiries) {
			Vue.set(state, 'inquiriesSummary', {
				...dashboardOrdersData.inquiries,
				inquiries: dashboardOrdersData.inquiries.inquiries.map((inquiry) => ({
					...inquiry,
					name: decodeString(inquiry.name || '')
				}))
			});
		}
	},

	SET_CALENDAR_TYPE(state, calendarType: 'month' | 'week' | 'day') {
		Vue.set(state, 'calendarType', calendarType);
	},

	SET_PENDING_ORDERS(state: DashboardState, pendingOrders: DashboardOrder[]) {
		if (state.pendingOrdersOffset === 0) {
			Vue.set(state, 'pendingOrders', pendingOrders);
		}
		else if ((state.pendingOrdersLimit + state.pendingOrdersOffset) > state.pendingOrders.length) {
			Vue.set(state, 'pendingOrders', [...state.pendingOrders, ...pendingOrders]);
		}
	},

	SET_PENDING_ORDERS_SORT(state, sortBy: 'created_at' | 'pickup_time') {
		Vue.set(state, 'pendingOrders', []);
		Vue.set(state, 'pendingOrdersOffset', 0);
		Vue.set(state, 'pendingOrdersLimit', PENDING_ORDERS_LIMIT);
		Vue.set(state, 'pendingOrdersSort', sortBy);
	},

	SET_PENDING_ORDERS_OFFSET(state, offset: number) {
		Vue.set(state, 'pendingOrdersOffset', offset);
	},

	SET_PENDING_ORDERS_LIMIT(state, limit: number) {
		Vue.set(state, 'pendingOrdersLimit', limit);
	},

	SET_QUOTES(state: DashboardState, quotes: DashboardQuoteOrder[]) {
		if (state.quotesOffset === 0) {
			Vue.set(state, 'quotes', quotes);
		}
		else if ((state.quotesLimit + state.quotesOffset) > state.quotes.length) {
			Vue.set(state, 'quotes', [...state.quotes, ...quotes]);
		}
	},

	SET_QUOTES_COUNT(state, count: number) {
		Vue.set(state, 'quotesCount', count);
	},

	SET_QUOTES_OFFSET(state, offset: number) {
		Vue.set(state, 'quotesOffset', offset);
	},

	SET_QUOTES_LIMIT(state, limit: number) {
		Vue.set(state, 'quotesLimit', limit);
	}
};